import { FunctionComponent } from 'react';
import { Button } from '../../tailwindcomponents';
import { Delete } from '../icons';

/**
 * Status key for the flight-schedule tables, shared by the Line and Lite/RVK pages.
 *
 * `color` is the exact row background the table paints for that status, so the key and
 * the table cannot drift apart. `null` means the row keeps the table's default
 * background - that entry renders a neutral swatch rather than inventing a hue.
 *
 * Accessibility notes, since these hues are domain-mandated and can't be re-picked:
 *  - every swatch is paired with its text label, so colour is never the only signal;
 *  - each swatch carries a hairline ring, because `lightyellow` on a white table is
 *    otherwise indistinguishable from an uncoloured row;
 *  - the dotted-line entry draws an actual dashed rule instead of spelling out the
 *    words "Dotted line", so the sample matches what the row border looks like.
 */
export const FLIGHT_STATUS_LEGEND: { color: string | null, label: string }[] = [
    { color: 'lightyellow', label: 'Aircraft in air' },
    { color: 'lightblue', label: 'Package in work' },
    { color: '#63AB62', label: 'All packages closed, ready for flight' },
    { color: null, label: 'No packages open' },
    // Tailwind red-300 - the same red as the overdue rows in Tool Stock.
    { color: '#FCA5A5', label: 'AOG' },
];

type FlightScheduleLegendProps = {
    /** Backend-formatted "Refreshed at ..." string. */
    dataFetched: string,
    /** Supplied only for supervisors; omit to hide the bulk-remove action. */
    onRemoveAllEmployees?: () => void,
}

const FlightScheduleLegend: FunctionComponent<FlightScheduleLegendProps> = ({ dataFetched, onRemoveAllEmployees }) => (
    <div className="ds-card px-3 py-2 mt-2 mb-2">
        <ul className="flex flex-wrap items-center gap-x-4 gap-y-1.5 m-0 p-0 list-none">
            {FLIGHT_STATUS_LEGEND.map(item => (
                <li key={item.label} className="flex items-center gap-1.5 text-xs">
                    <span
                        className="h-3 w-3 shrink-0 rounded-full ring-1 ring-black/25"
                        style={{ backgroundColor: item.color ?? 'transparent' }}
                        aria-hidden="true"
                    />
                    {item.label}
                </li>
            ))}
            <li className="flex items-center gap-1.5 text-xs">
                <span className="h-0 w-6 shrink-0 border-t-2 border-dashed border-gray-500" aria-hidden="true" />
                Incomplete paperwork in MXI
            </li>
        </ul>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line mt-1.5 pt-1.5">
            <span className="text-xs text-muted">{dataFetched} &middot; press R to refresh</span>
            {onRemoveAllEmployees !== undefined &&
                <Button variant="text" color="error" size="small" startIcon={<Delete />} onClick={onRemoveAllEmployees}>
                    Remove all of your employees
                </Button>
            }
        </div>
    </div>
);

export default FlightScheduleLegend;
